import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import type { DocNode } from '@atlaskit/adf-schema';

import {
	ExperienceName as RovoExperienceName,
	useAnalytics,
} from '@atlassian/conversation-assistant-instrumentation';
import { extractContentFeatures } from '@atlassian/conversation-assistant';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store';
import { sendAIFCExperienceTrackingToParent } from '@atlassian/rovo-content-bridge-api-experience-tracking-sender';

import { isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import {
	showCreateWithRovoModalCloseFeedbackFlag,
	FeedbackVariant,
} from '@confluence/smart-create/entry-points/showFeedbackFlag';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore';

import { log } from '../utils/aifc-logging';
import { getDocument } from '../utils/bridge-document-utils';
import { useIsPageViewMode } from '../utils/useIsPageViewMode';
import type {
	ErrorCallbackParams,
	PassThroughCommandProperties,
	RepairCallbackParams,
	StreamCallback,
	StreamCallbackParams,
	StreamEndCallbackParams,
	StreamStartCallbackParams,
} from '../command-handlers/types/command-handler-types';
import { RovoBridgeCommandManager } from '../bridge-management/rovo-bridge-command-manager';
import type { UseRovoBridgeCommandListenerParams } from '../RovoBridgeCommandListener';

import { useMediaImageProcessor } from './useMediaImageProcessor';

const scheduleTeardown = (teardown: () => void) => {
	const run = () => {
		/**
		 * @description We do NOT want to yield a return value here. It can cause certain unload
		 * handlers to display a window alert with "Are you sure you want to leave this page?".
		 * Specifically, see @see https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event,
		 * which is remarked to be a cancellable event by browsers.
		 */
		teardown();
		// Although unnecessary (the JS context is being destroyed), we remove the event listeners
		// to avoid any potential memory leaks due to hanging references - side effects can cause this.
		window.removeEventListener('unload', run);
		window.removeEventListener('pagehide', run);
	};

	// Below, we attempt to teardown on different event handlers which we know will trigger
	// on complete unmount of the document in this JS context. Note: Same-origin iframes are
	// indeed considered their own JS context here; just that they have access to other contexts.
	// Browsers report `unload` and `beforeunload` to be unreliable. Hence, we leverage a chain
	// of these events. See official MDN documentation for more (linked below).

	/**
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
	 * TODO - bring beforeunload back by consolidating handlers per discussion at -
	 * https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/207747/overview#comment-674441389
	 */
	// window.addEventListener('beforeunload', run, { once: true });
	/**
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/unload_event
	 */
	window.addEventListener('unload', run, { once: true });
	/**
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/pagehide_event
	 */
	window.addEventListener('pagehide', run, { once: true });
};

export const useRovoBridgeCommandListener = ({
	editorAPI,
	onTitleChange,
	contentId,
	setEditorDisabled,
	forceDraftSync,
	generateAIHeaderImage,
	fakeGenerateAIHeaderImage,
	contentContext,
	mediaProvider,
	setTitlePlaceholderState,
	flags,
}: UseRovoBridgeCommandListenerParams & WithFlagsProps) => {
	const { sendAnalyticsEvent } = useAnalytics();
	const isViewMode = useIsPageViewMode();
	const intl = useIntl();
	const { showModal } = useDialogs();
	const { showSmartCreationModal } = useSmartCreationModalState();
	const { stagingAreaMode } = useStagingAreaState();

	const imageProcessor = useMediaImageProcessor({
		mediaProvider,
	});

	const sendExperienceSuccess = useCallback(() => {
		void sendAIFCExperienceTrackingToParent({
			status: 'success',
			actionType: 'page',
			actionId: contentId,
			contentContext,
			experienceName: RovoExperienceName.AIFC_ALL_DATA_HANDLED,
		});
	}, [contentId, contentContext]);

	const reuploadImages = useCallback(
		async (adfDoc: DocNode): Promise<void> => {
			const processImagesAndUpdateDocument = async () => {
				if (!imageProcessor?.processImagesInAdf) {
					throw new Error(
						'Image processor not available for image re-uploading in editor streaming',
					);
				}
				if (!editorAPI) {
					throw new Error('Editor API not available for image re-uploading in editor streaming');
				}

				const { error, hasChanges, processedAdf } = await imageProcessor.processImagesInAdf(adfDoc);

				if (error) {
					throw error;
				}

				if (hasChanges) {
					editorAPI?.core.actions.replaceDocument(processedAdf, {
						scrollIntoView: false,
					});
				}
			};

			try {
				await processImagesAndUpdateDocument();
			} catch (error) {
				void sendAIFCExperienceTrackingToParent({
					status: 'fail',
					actionType: 'page',
					actionId: contentId,
					contentContext,
					experienceName: RovoExperienceName.AIFC_ALL_DATA_HANDLED,
					error: (error as Error).message,
				});

				getMonitoringClient().submitError(error as Error, {
					attribution: Attribution.PAGE_MODES,
				});
			}
		},
		[imageProcessor, editorAPI, contentId, contentContext],
	);

	const sendInitialCreationAnalytics = useCallback(async () => {
		try {
			let features;
			if (editorAPI) {
				const doc = await getDocument(editorAPI);
				if (doc) {
					features = extractContentFeatures(doc);
				}
			}

			sendAnalyticsEvent({
				eventType: 'track',
				actionSubject: 'smartCreateContent',
				action: 'initialContentCreated',
				attributes: {
					createdContentProduct: 'confluence',
					createdContentType: 'page',
					createdContentFeatures: features,
					createdContentMetadata: {
						contentId,
					},
				},
			});
		} catch (error) {
			getMonitoringClient().submitError(error, { attribution: Attribution.PAGE_MODES });
		}
	}, [editorAPI, contentId, sendAnalyticsEvent]);

	// Called once when streaming starts
	const onStreamStart = useCallback(
		({ streamType }: StreamStartCallbackParams) => {
			setEditorDisabled(true);

			// Succeed the AIFC_INITIAL_DATA_RECEIVED experience since content has started streaming.
			// This ensures this experience won't timeout and then fail the AIFC_GENERATE_CONTENT or AIFC_UPDATE_CONTENT experience.
			// NOTE: This experience isn't started for all flows, it'll be a no-op if it's not started.
			// This ensures this experience won't timeout and then fail the AIFC_GENERATE_CONTENT experience.
			void sendAIFCExperienceTrackingToParent({
				status: 'success',
				actionType: 'page',
				actionId: contentId,
				contentContext,
				experienceName: RovoExperienceName.AIFC_INITIAL_DATA_RECEIVED,
			});

			if (streamType === 'create' || streamType === 'update') {
				setTitlePlaceholderState?.(true);
				if (isExperimentEnabled('confluence_aifc_image_generation_experiment')) {
					if (!fg('aifc_header_image_pseudo_title_enabled')) {
						void fakeGenerateAIHeaderImage?.();
					}
				}
			}
		},
		[
			setEditorDisabled,
			fakeGenerateAIHeaderImage,
			setTitlePlaceholderState,
			contentId,
			contentContext,
		],
	);

	// Called on every stream chunk
	const onStream: StreamCallback = useCallback(
		({ params }: StreamCallbackParams) => {
			if ('title' in params.data) {
				const title = params?.data?.title;
				onTitleChange?.(title);
				if (title) {
					setTitlePlaceholderState?.(false);
				}
			}
		},
		[onTitleChange, setTitlePlaceholderState],
	);

	// Called when streaming ends
	const onStreamEnd = useCallback(
		({ streamType, params }: StreamEndCallbackParams) => {
			try {
				if (streamType === 'create') {
					const title = 'title' in params?.data ? params.data.title : undefined;

					if (title) {
						if (isExperimentEnabled('confluence_aifc_image_generation_experiment')) {
							if (!fg('aifc_header_image_pseudo_title_enabled')) {
								void generateAIHeaderImage?.({ adfContent: title });
							}
						}
					}

					void sendInitialCreationAnalytics();
				} else if (streamType === 'update') {
					if (!showSmartCreationModal && stagingAreaMode !== 'modal') {
						showCreateWithRovoModalCloseFeedbackFlag(
							flags,
							intl,
							showModal,
							FeedbackVariant.AFTER_UPDATE_STREAM_COMPLETE,
						);
					}
				}
			} finally {
				setEditorDisabled(false);
			}
		},
		[
			setEditorDisabled,
			generateAIHeaderImage,
			sendInitialCreationAnalytics,
			flags,
			intl,
			showModal,
			showSmartCreationModal,
			stagingAreaMode,
		],
	);

	const onStreamError = useCallback(
		({ error, streamType }: ErrorCallbackParams) => {
			log('Error in command handler:', error);

			sendAnalyticsEvent({
				eventType: 'operational',
				action: 'error',
				actionSubject: 'smartCreateContent',
				attributes: {
					contentType: 'page',
					error: error instanceof Error ? error.message : String(error),
					stack: error instanceof Error && error.stack ? error.stack : undefined,
					streamType,
					contentId,
				},
			});

			void sendAIFCExperienceTrackingToParent({
				status: 'fail',
				actionType: 'page',
				actionId: contentId,
				contentContext,
				experienceName: RovoExperienceName.AIFC_ALL_DATA_HANDLED,
				error: error instanceof Error ? error.message : String(error),
			});
		},
		[contentId, contentContext, sendAnalyticsEvent],
	);

	const onStreamRepair = useCallback(
		({ streamType, repairs }: RepairCallbackParams) => {
			sendAnalyticsEvent({
				eventType: 'operational',
				actionSubject: 'smartCreateContent',
				action: 'adfRepaired',
				attributes: {
					contentType: 'page',
					contentId,
					streamType,
					repairCount: repairs.length,
					// Manually selecting properties to avoid sending UGC
					// in case more properties are added in the future.
					repairs: repairs.map((repair) => ({
						type: repair.type,
						from: repair.from,
						to: repair.to,
						context: repair.context,
						expansion: repair.expansion,
					})),
				},
			});
		},
		[contentId, sendAnalyticsEvent],
	);

	const listener = useMemo(() => {
		if (!editorAPI) {
			return null;
		}

		return new RovoBridgeCommandManager(contentId, contentContext, editorAPI);
	}, [contentId, contentContext, editorAPI]);

	useEffect(() => {
		if (!listener || !editorAPI) {
			return;
		}

		const commandProperties: PassThroughCommandProperties = {
			editorAPI,
			contentId,
			onStreamStart,
			onStream,
			onStreamEnd,
			onStreamError,
			onStreamRepair,
			forceDraftSync,
			generateAIHeaderImage,
			setEditorDisabled,
			reuploadImages,
			sendExperienceSuccess,
			isViewMode,
		};
		listener.registerCommands(commandProperties);

		return () => {
			listener.deregisterCommands();
		};
	}, [
		listener,
		editorAPI,
		contentId,
		forceDraftSync,
		onStreamStart,
		onStream,
		onStreamEnd,
		onStreamError,
		onStreamRepair,
		generateAIHeaderImage,
		setEditorDisabled,
		reuploadImages,
		sendExperienceSuccess,
		isViewMode,
	]);

	useEffect(() => {
		if (!listener) {
			return;
		}

		listener.ready();
		scheduleTeardown(listener.teardown.bind(listener));

		return () => {
			try {
				listener.teardown();
				// TODO: clear inactivity timeouts?
			} catch {
				// TODO - handle errors; should go to Sentry
				// could be sent in future by navigation lockout
			}
		};
	}, [listener]);
};
